/**
 * Power and Cooling Calculations for hermit-μDC
 *
 * Provides estimates for:
 * - Total power draw (watts)
 * - Heat output (BTU/hr)
 * - Cooling requirements (tons of cooling)
 * - PDU sizing
 * - UPS runtime
 * - Electrical circuit requirements
 */

import { DatacenterNode, InfrastructureUnit, ContainerConfig } from '../types/configurator';

// ============================================
// CONSTANTS
// ============================================

// Power conversion factors
const WATTS_TO_BTU_HR = 3.412; // 1W = 3.412 BTU/hr
const BTU_HR_TO_TONS = 12000; // 12,000 BTU/hr = 1 ton of cooling
const PUE_TYPICAL = 1.5; // Power Usage Effectiveness (datacenter overhead)
const PUE_CONTAINER = 1.3; // Container datacenter

// Electrical standards
const VOLTAGE_US = 120;
const VOLTAGE_EU = 230;
const VOLTAGE_DATACENTER = 208; // 3-phase

// Safety margins
const POWER_SAFETY_MARGIN = 1.2; // 20% headroom
const COOLING_SAFETY_MARGIN = 1.25; // 25% headroom

// ============================================
// INTERFACES
// ============================================

export interface PowerCalculation {
  // Power draw
  totalWatts: number;
  peakWatts: number; // With safety margin
  itLoadWatts: number; // Just IT equipment
  coolingWatts: number; // Cooling system power
  overheadWatts: number; // Lighting, controls, etc.

  // Per-node breakdown
  nodeBreakdown: { name: string; watts: number; percentage: number }[];

  // Heat output
  btuPerHour: number;
  tonsOfCooling: number;
  recommendedCoolingTons: number; // With safety margin

  // Electrical requirements
  ampsAt120V: number;
  ampsAt208V: number;
  ampsAt230V: number;
  circuitsNeeded20A: number;
  circuitsNeeded30A: number;

  // UPS sizing
  recommendedUpsVA: number;
  runtimeMinutesAt50Percent: number;
  runtimeMinutesAt100Percent: number;

  // PDU requirements
  recommendedPduAmps: number;
  outletsNeeded: number;

  // Efficiency metrics
  pue: number;
  annualKwh: number;
  annualCostUSD: number; // At $0.12/kWh

  // Warnings
  warnings: string[];
}

export interface CoolingRecommendation {
  type: 'portable' | 'split' | 'inrow' | 'crac' | 'none';
  name: string;
  btuCapacity: number;
  quantity: number;
  totalCost: number;
  description: string;
}

// ============================================
// POWER CALCULATIONS
// ============================================

export function calculatePowerRequirements(
  nodes: DatacenterNode[],
  infrastructure: InfrastructureUnit[],
  containerConfig?: ContainerConfig
): PowerCalculation {
  const warnings: string[] = [];

  // Calculate IT load from nodes
  let itLoadWatts = 0;
  const nodeBreakdown: { name: string; watts: number; percentage: number }[] = [];

  for (const node of nodes) {
    let nodeWatts = node.specs.powerWatts;

    // Add GPU power if present
    if (node.specs.gpu) {
      nodeWatts += node.specs.gpu.tdpWatts;
    }

    itLoadWatts += nodeWatts;
    nodeBreakdown.push({
      name: node.name,
      watts: nodeWatts,
      percentage: 0, // Will calculate after total
    });
  }

  // Calculate percentages
  for (const item of nodeBreakdown) {
    item.percentage = itLoadWatts > 0 ? (item.watts / itLoadWatts) * 100 : 0;
  }

  // Calculate infrastructure power
  let infraWatts = 0;
  for (const infra of infrastructure) {
    infraWatts += infra.specs.powerWatts;
  }

  // Determine PUE based on deployment type
  const pue = containerConfig ? PUE_CONTAINER : PUE_TYPICAL;

  // Total power calculations
  const totalWatts = itLoadWatts + infraWatts;
  const coolingWatts = itLoadWatts * (pue - 1) * 0.7; // ~70% of overhead is cooling
  const overheadWatts = itLoadWatts * (pue - 1) * 0.3; // 30% is other overhead
  const peakWatts = totalWatts * POWER_SAFETY_MARGIN;

  // Heat calculations
  const btuPerHour = totalWatts * WATTS_TO_BTU_HR;
  const tonsOfCooling = btuPerHour / BTU_HR_TO_TONS;
  const recommendedCoolingTons = tonsOfCooling * COOLING_SAFETY_MARGIN;

  // Electrical calculations
  const ampsAt120V = peakWatts / VOLTAGE_US;
  const ampsAt208V = peakWatts / VOLTAGE_DATACENTER;
  const ampsAt230V = peakWatts / VOLTAGE_EU;

  // Circuit calculations (with 80% derating)
  const circuitsNeeded20A = Math.ceil(ampsAt120V / (20 * 0.8));
  const circuitsNeeded30A = Math.ceil(ampsAt208V / (30 * 0.8));

  // UPS sizing (VA = Watts / power factor, assume 0.9 PF)
  const recommendedUpsVA = Math.ceil((peakWatts / 0.9) / 1000) * 1000; // Round to nearest 1000VA

  // UPS runtime estimates (typical battery)
  const runtimeMinutesAt50Percent = estimateUpsRuntime(recommendedUpsVA, totalWatts * 0.5);
  const runtimeMinutesAt100Percent = estimateUpsRuntime(recommendedUpsVA, totalWatts);

  // PDU sizing
  const recommendedPduAmps = Math.ceil(ampsAt208V / 0.8 / 10) * 10; // Round to 10A increments
  const outletsNeeded = nodes.length + Math.ceil(infrastructure.length / 2);

  // Annual energy
  const annualKwh = (totalWatts * pue * 8760) / 1000; // 8760 hours/year
  const annualCostUSD = annualKwh * 0.12; // $0.12/kWh average

  // Warnings
  if (totalWatts > 10000) {
    warnings.push('High power deployment (>10kW) - consider 3-phase power');
  }
  if (tonsOfCooling > 3) {
    warnings.push('Significant cooling required - dedicated HVAC recommended');
  }
  if (nodes.some(n => n.specs.gpu && n.specs.gpu.tdpWatts > 300)) {
    warnings.push('High-power GPUs present - ensure adequate airflow');
  }
  if (ampsAt120V > 100) {
    warnings.push('Exceeds single residential circuit - commercial power required');
  }

  return {
    totalWatts,
    peakWatts,
    itLoadWatts,
    coolingWatts,
    overheadWatts,
    nodeBreakdown,
    btuPerHour,
    tonsOfCooling,
    recommendedCoolingTons,
    ampsAt120V,
    ampsAt208V,
    ampsAt230V,
    circuitsNeeded20A,
    circuitsNeeded30A,
    recommendedUpsVA,
    runtimeMinutesAt50Percent,
    runtimeMinutesAt100Percent,
    recommendedPduAmps,
    outletsNeeded,
    pue,
    annualKwh,
    annualCostUSD,
    warnings,
  };
}

function estimateUpsRuntime(upsVA: number, loadWatts: number): number {
  // Simplified runtime estimation based on typical battery curves
  const loadPercent = (loadWatts / (upsVA * 0.9)) * 100;

  if (loadPercent <= 25) return 60;
  if (loadPercent <= 50) return 20;
  if (loadPercent <= 75) return 10;
  if (loadPercent <= 100) return 5;
  return 2;
}

// ============================================
// COOLING RECOMMENDATIONS
// ============================================

export function recommendCooling(powerCalc: PowerCalculation): CoolingRecommendation[] {
  const recommendations: CoolingRecommendation[] = [];
  const requiredBtu = powerCalc.btuPerHour * COOLING_SAFETY_MARGIN;

  if (requiredBtu < 5000) {
    // Very small - natural convection may suffice
    recommendations.push({
      type: 'none',
      name: 'Natural Convection',
      btuCapacity: 5000,
      quantity: 0,
      totalCost: 0,
      description: 'Low heat load - ensure good ventilation',
    });
  }

  if (requiredBtu < 12000) {
    // Small - portable AC
    recommendations.push({
      type: 'portable',
      name: 'Portable AC Unit',
      btuCapacity: 12000,
      quantity: 1,
      totalCost: 600,
      description: 'Single portable spot cooler',
    });
  }

  if (requiredBtu >= 12000 && requiredBtu < 36000) {
    // Medium - split system or multiple portables
    const portableCount = Math.ceil(requiredBtu / 12000);
    recommendations.push({
      type: 'portable',
      name: 'Portable AC Units',
      btuCapacity: 12000,
      quantity: portableCount,
      totalCost: portableCount * 600,
      description: `${portableCount}x portable spot coolers`,
    });

    recommendations.push({
      type: 'split',
      name: 'Mini-Split System',
      btuCapacity: 36000,
      quantity: 1,
      totalCost: 3500,
      description: 'Wall-mounted split system with external condenser',
    });
  }

  if (requiredBtu >= 36000 && requiredBtu < 100000) {
    // Large - split or in-row
    recommendations.push({
      type: 'split',
      name: 'Commercial Split System',
      btuCapacity: 60000,
      quantity: Math.ceil(requiredBtu / 60000),
      totalCost: Math.ceil(requiredBtu / 60000) * 8000,
      description: 'Commercial-grade split cooling',
    });

    recommendations.push({
      type: 'inrow',
      name: 'In-Row Cooling Unit',
      btuCapacity: 68000,
      quantity: Math.ceil(requiredBtu / 68000),
      totalCost: Math.ceil(requiredBtu / 68000) * 12000,
      description: 'Rack-style in-row precision cooling',
    });
  }

  if (requiredBtu >= 100000) {
    // Very large - CRAC unit
    recommendations.push({
      type: 'crac',
      name: 'Computer Room AC (CRAC)',
      btuCapacity: 170000,
      quantity: Math.ceil(requiredBtu / 170000),
      totalCost: Math.ceil(requiredBtu / 170000) * 25000,
      description: 'Precision datacenter cooling',
    });
  }

  return recommendations;
}

// ============================================
// CIRCUIT BREAKER SIZING
// ============================================

export interface CircuitRecommendation {
  voltage: number;
  amperage: number;
  phase: '1-phase' | '3-phase';
  quantity: number;
  wireGauge: string;
  description: string;
}

export function recommendCircuits(powerCalc: PowerCalculation): CircuitRecommendation[] {
  const recommendations: CircuitRecommendation[] = [];

  if (powerCalc.peakWatts < 2400) {
    // Standard 20A circuits
    recommendations.push({
      voltage: 120,
      amperage: 20,
      phase: '1-phase',
      quantity: powerCalc.circuitsNeeded20A,
      wireGauge: '12 AWG',
      description: 'Standard residential circuits',
    });
  }

  if (powerCalc.peakWatts >= 2400 && powerCalc.peakWatts < 7200) {
    // 30A circuits
    recommendations.push({
      voltage: 208,
      amperage: 30,
      phase: '1-phase',
      quantity: powerCalc.circuitsNeeded30A,
      wireGauge: '10 AWG',
      description: 'Commercial-grade circuits',
    });
  }

  if (powerCalc.peakWatts >= 7200) {
    // 3-phase recommended
    const ampsPerPhase = powerCalc.peakWatts / (208 * 1.732);
    recommendations.push({
      voltage: 208,
      amperage: Math.ceil(ampsPerPhase / 10) * 10,
      phase: '3-phase',
      quantity: 1,
      wireGauge: ampsPerPhase > 60 ? '4 AWG' : '8 AWG',
      description: '3-phase power distribution',
    });
  }

  return recommendations;
}

// ============================================
// SUMMARY FORMATTING
// ============================================

export function formatPowerSummary(calc: PowerCalculation): string {
  return `
Power Requirements Summary
==========================

IT Load:        ${calc.itLoadWatts.toLocaleString()} W
Total Power:    ${calc.totalWatts.toLocaleString()} W
Peak (w/margin): ${calc.peakWatts.toLocaleString()} W

Heat Output:    ${Math.round(calc.btuPerHour).toLocaleString()} BTU/hr
Cooling Need:   ${calc.recommendedCoolingTons.toFixed(2)} tons

Electrical:
  @ 120V: ${calc.ampsAt120V.toFixed(1)} A (${calc.circuitsNeeded20A}x 20A circuits)
  @ 208V: ${calc.ampsAt208V.toFixed(1)} A (${calc.circuitsNeeded30A}x 30A circuits)

UPS Sizing:     ${(calc.recommendedUpsVA / 1000).toFixed(1)} kVA
PDU Sizing:     ${calc.recommendedPduAmps} A

Annual Energy:  ${Math.round(calc.annualKwh).toLocaleString()} kWh
Annual Cost:    $${Math.round(calc.annualCostUSD).toLocaleString()}

PUE:            ${calc.pue.toFixed(2)}
`.trim();
}
